import React, { useEffect, useRef, useState } from 'react';
import { FiPlay, FiRefreshCw, FiDownload, FiAlertTriangle, FiInfo, FiCheckCircle } from 'react-icons/fi';
import type { CarrierAction } from './carrierAccessCatalog';
import { FalconQuoteForm, HarborQuoteForm, AssuranceQuoteForm, MercuryQuoteForm, ProgressiveQuoteForm, NationalGeneralQuoteForm, LibertyMutualQuoteForm, TravelersQuoteForm, OpenlyQuoteForm, ProgressiveHomeQuoteForm } from './CarrierQuoteForms';
import { runAndWait, resolveDownloadUrl, submitTwoFactorCode, type JobStatus, type CarrierPolicy } from '../../services/automationService';

interface CarrierActionRunnerProps {
  carrierId: string;
  /** The signed-in agency — so a job run uses this agency's own saved portal login (see /agency-credentials) instead of always falling back to .env.local. */
  agencyId: string;
  action: CarrierAction;
  policies: CarrierPolicy[];
  onPoliciesLoaded: (policies: CarrierPolicy[]) => void;
  /** The policy number last clicked in this carrier's "View Policy List" cards, shared across its actions — "Retrieve a Document" pre-fills from this. */
  selectedPolicyNumber?: string;
  /** Call when a policy card is clicked, so "Retrieve a Document" (a separate action instance) can pick it up. */
  onPolicySelected?: (policyNumber: string) => void;
  /**
   * Carrier-level preference (set once in the carrier's detail panel, not
   * per-quote) — when true, Create Quote runs in a visible browser and
   * leaves it open at the end, success or stuck, so the agency can take
   * over and click Bind itself. Never applies to anything but 'quote'
   * actions.
   */
  headed?: boolean;
}

type RunState =
  | { phase: 'idle' }
  | { phase: 'running'; status: JobStatus }
  | { phase: 'done'; status: JobStatus }
  | { phase: 'error'; message: string };

const IDLE_STATUS: JobStatus = { status: 'running', log: '', resultPath: null, policiesJson: null, attentionMessage: null, resumed: false, label: '' };

const QUOTE_FORM_BY_CARRIER: Record<string, React.FC<{ onSubmit: (payload: object) => void; disabled: boolean }>> = {
  falcon: FalconQuoteForm,
  harbor: HarborQuoteForm,
  assuranceAmerica: AssuranceQuoteForm,
  mercury: MercuryQuoteForm,
  progressive: ProgressiveQuoteForm,
  nationalGeneral: NationalGeneralQuoteForm,
  libertyMutual: LibertyMutualQuoteForm,
  travelers: TravelersQuoteForm,
  openly: OpenlyQuoteForm,
};

const CarrierActionRunner: React.FC<CarrierActionRunnerProps> = ({
  carrierId,
  agencyId,
  action,
  policies,
  onPoliciesLoaded,
  selectedPolicyNumber,
  onPolicySelected,
  headed,
}) => {
  const [run, setRun] = useState<RunState>({ phase: 'idle' });
  const [policyNumber, setPolicyNumber] = useState('');
  // Carrier-browser tools: the last name typed into "Find a Policy by Last Name", the inputs of the last run (so a
  // retry after logging in to the carrier browser repeats exactly it), and the "opening the browser" spinner.
  const [lastName, setLastName] = useState('');
  // A 'lookup' action's inputs, by field name; selects start on their first option.
  const [lookupValues, setLookupValues] = useState<Record<string, string>>(() =>
    action.type === 'lookup' ? Object.fromEntries(action.fields.map((f) => [f.name, f.kind === 'select' ? f.options?.[0]?.value ?? '' : ''])) : {},
  );
  const [lastArgs, setLastArgs] = useState<string[]>([]);
  const [browserOpening, setBrowserOpening] = useState(false);
  // 2FA code entry for a run paused on needs_2fa_code (see submitTwoFactorCode).
  const [runId, setRunId] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorSubmitting, setTwoFactorSubmitting] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);

  // A card clicked in "View Policy List" (a separate action instance) feeds
  // its policy number in here via the shared parent state — sync whenever
  // it changes so "Retrieve a Document" pre-fills without a manual search.
  useEffect(() => {
    if (action.type === 'document' && selectedPolicyNumber) {
      setPolicyNumber(selectedPolicyNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicyNumber]);
  const [documentType, setDocumentType] = useState(action.type === 'document' ? action.documentTypes[0]?.key ?? '' : '');
  // The policy currently picked in the dropdown — shows its details below
  // and feeds "Retrieve a Document" via onPolicySelected.
  const [selectedPolicy, setSelectedPolicy] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [search, setSearch] = useState('');
  // Toggling this action off (or disconnecting the carrier) unmounts this
  // component mid-run — the backend job keeps going, but nothing here
  // should call setState after that point.
  // Setup must set this true (not just rely on the useRef initial value) —
  // StrictMode's dev-mode setup/cleanup/setup replay would otherwise leave
  // it stuck false after the simulated remount, silently dropping every
  // later setRun call including the terminal one.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const runJob = async (extraArgs: string[]) => {
    setLastArgs(extraArgs);
    setRun({ phase: 'running', status: { ...IDLE_STATUS, label: action.label } });
    setRunId(null);
    setTwoFactorCode('');
    setTwoFactorError(null);
    try {
      const final = await runAndWait(action.jobKey, extraArgs, (status, id) => {
        if (!isMountedRef.current) return;
        setRunId(id);
        setRun({ phase: 'running', status });
      }, agencyId);
      if (!isMountedRef.current) return;
      if (final.status === 'completed' && action.type === 'policies') {
        // Some carriers (Liberty Mutual) report the insured as customerName
        // rather than name — fill name in so search/display don't break.
        onPoliciesLoaded((final.policiesJson ?? []).map((p) => ({ ...p, name: p.name ?? String(p.customerName ?? '') })));
      }
      setRun({ phase: 'done', status: final });
    } catch (err) {
      if (isMountedRef.current) {
        setRun({ phase: 'error', message: err instanceof Error ? err.message : 'The run failed unexpectedly.' });
      }
    }
  };

  // 'policies' actions load automatically as soon as this panel opens —
  // no manual "Refresh & View" click required — but only when nothing is
  // cached yet. `policies` is lifted to the parent (CarrierAccessPage) and
  // survives this component unmounting when you collapse the Automations
  // dropdown or toggle the action off, so re-expanding the same carrier
  // shows what's already there instantly instead of re-running a full
  // login + fetch (a real cost for carriers like Progressive, whose policy
  // list is a paginated 332-record pull). The refresh icon still forces a
  // real re-fetch on demand. Must stay above the early return below —
  // hooks can't be called conditionally.
  useEffect(() => {
    if (action.type === 'policies' && policies.length === 0) {
      runJob([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action.jobKey]);

  if (action.type === 'quote' && action.disabled) {
    return <div className="ca-run ca-run--info">{action.disabledReason}</div>;
  }

  const isRunning = run.phase === 'running';
  const datalistId = `ca-policies-${carrierId}`;

  const handleRunDocument = () => {
    if (!policyNumber.trim()) {
      setRun({ phase: 'error', message: 'Enter a policy number first.' });
      return;
    }
    runJob([policyNumber.trim(), documentType]);
  };

  const handleRunSearch = () => {
    if (!lastName.trim()) {
      setRun({ phase: 'error', message: 'Enter a last name first.' });
      return;
    }
    runJob([lastName.trim()]);
  };

  const handleRunLookup = () => {
    if (action.type !== 'lookup') return;
    const missing = action.fields.find((f) => f.required && !(lookupValues[f.name] ?? '').trim());
    if (missing) {
      setRun({ phase: 'error', message: `Enter ${missing.label.toLowerCase()} first.` });
      return;
    }
    const args = [...(action.fixedArgs ?? []), ...action.fields.map((f) => (lookupValues[f.name] ?? '').trim())];
    while (args.length > 0 && args[args.length - 1] === '') args.pop();
    runJob(args);
  };

  const handleRunPolicyDocuments = () => {
    if (!policyNumber.trim()) {
      setRun({ phase: 'error', message: 'Enter a policy number first.' });
      return;
    }
    runJob([policyNumber.trim()]);
  };

  // A run came back needs_manual_login: opens (or re-fronts) the carrier browser on the machine running the
  // automation server so a person can log in there by hand. Only opens the window — the retry is a separate click.
  const handleOpenCarrierBrowser = async () => {
    setBrowserOpening(true);
    try {
      await runAndWait('carrier-browser-open', []);
    } catch {
      // The retry surfaces any real problem.
    }
    if (isMountedRef.current) setBrowserOpening(false);
  };

  // The home quote has its own form (Progressive Homeowners).
  const isHomeQuote = action.type === 'quote' && action.key === 'createHomeQuote';
  const QuoteForm = action.type !== 'quote' ? null : isHomeQuote ? ProgressiveHomeQuoteForm : QUOTE_FORM_BY_CARRIER[carrierId];

  const handleSubmitTwoFactor = async () => {
    if (!runId || !twoFactorCode.trim()) return;
    setTwoFactorSubmitting(true);
    setTwoFactorError(null);
    try {
      await submitTwoFactorCode(runId, twoFactorCode.trim(), agencyId);
      if (isMountedRef.current) setTwoFactorCode('');
    } catch (err) {
      if (isMountedRef.current) setTwoFactorError(err instanceof Error ? err.message : 'Could not submit the code.');
    } finally {
      if (isMountedRef.current) setTwoFactorSubmitting(false);
    }
  };

  const filteredPolicies = search.trim()
    ? policies.filter((p) => {
        const q = search.trim().toLowerCase();
        return p.policyNumber.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
      })
    : policies;

  const selectedPolicyRecord = selectedPolicy ? policies.find((p) => p.policyNumber === selectedPolicy) ?? null : null;

  return (
    <div className="ca-run">
      {action.type === 'document' && (
        <div className="ca-run__form">
          <input
            className="ca-run__input"
            placeholder="Policy number"
            value={policyNumber}
            disabled={isRunning}
            list={policies.length > 0 ? datalistId : undefined}
            onChange={(e) => setPolicyNumber(e.target.value)}
          />
          {policies.length > 0 && (
            <datalist id={datalistId}>
              {policies.map((p) => (
                <option key={p.policyNumber} value={p.policyNumber}>{p.name}</option>
              ))}
            </datalist>
          )}
          <select
            className="ca-run__select"
            value={documentType}
            disabled={isRunning}
            onChange={(e) => setDocumentType(e.target.value)}
          >
            {action.documentTypes.map((doc) => (
              <option key={doc.key} value={doc.key}>{doc.label}</option>
            ))}
          </select>
          <button type="button" className="ca-run__btn" onClick={handleRunDocument} disabled={isRunning}>
            {isRunning ? <FiRefreshCw size={13} className="spin" /> : <FiPlay size={13} />}
            {isRunning ? 'Running…' : 'Retrieve'}
          </button>
        </div>
      )}

      {action.type === 'lookup' && (
        <div className="ca-run__form">
          {action.fields.map((f) =>
            f.kind === 'select' ? (
              <select
                key={f.name}
                className="ca-run__select"
                aria-label={f.label}
                value={lookupValues[f.name] ?? ''}
                disabled={isRunning}
                onChange={(e) => setLookupValues((v) => ({ ...v, [f.name]: e.target.value }))}
              >
                {(f.options ?? []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input
                key={f.name}
                className="ca-run__input"
                aria-label={f.label}
                placeholder={f.placeholder ?? f.label}
                value={lookupValues[f.name] ?? ''}
                disabled={isRunning}
                onChange={(e) => setLookupValues((v) => ({ ...v, [f.name]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRunLookup(); }}
              />
            ),
          )}
          <button type="button" className="ca-run__btn" onClick={handleRunLookup} disabled={isRunning}>
            {isRunning ? <FiRefreshCw size={13} className="spin" /> : <FiPlay size={13} />}
            {isRunning ? 'Loading…' : action.submitLabel ?? 'Run'}
          </button>
        </div>
      )}

      {action.type === 'policySearch' && (
        <div className="ca-run__form">
          <input
            className="ca-run__input"
            placeholder="Last name"
            value={lastName}
            disabled={isRunning}
            onChange={(e) => setLastName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRunSearch(); }}
          />
          <button type="button" className="ca-run__btn" onClick={handleRunSearch} disabled={isRunning}>
            {isRunning ? <FiRefreshCw size={13} className="spin" /> : <FiPlay size={13} />}
            {isRunning ? 'Searching…' : 'Search'}
          </button>
        </div>
      )}

      {action.type === 'policyDocuments' && (
        <div className="ca-run__form">
          <input
            className="ca-run__input"
            placeholder="Policy number"
            value={policyNumber}
            disabled={isRunning}
            list={policies.length > 0 ? datalistId : undefined}
            onChange={(e) => setPolicyNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRunPolicyDocuments(); }}
          />
          {policies.length > 0 && (
            <datalist id={datalistId}>
              {policies.map((p) => (
                <option key={p.policyNumber} value={p.policyNumber}>{p.name}</option>
              ))}
            </datalist>
          )}
          <button type="button" className="ca-run__btn" onClick={handleRunPolicyDocuments} disabled={isRunning}>
            {isRunning ? <FiRefreshCw size={13} className="spin" /> : <FiPlay size={13} />}
            {isRunning ? 'Loading…' : 'List Documents'}
          </button>
        </div>
      )}

      {action.type === 'policies' && (
        <div className="ca-run__policies-header">
          <span className="ca-run__policies-header-label">
            {isRunning
              ? 'Loading policies…'
              : search.trim()
                ? `${filteredPolicies.length} of ${policies.length}`
                : `${policies.length} polic${policies.length === 1 ? 'y' : 'ies'}`}
          </span>
          <div className="ca-run__policies-header-actions">
            <button
              type="button"
              className="ca-run__icon-btn"
              onClick={() => setShowInfo((v) => !v)}
              aria-label="What does this do?"
              title="What does this do?"
            >
              <FiInfo size={14} />
            </button>
            <button
              type="button"
              className="ca-run__icon-btn"
              onClick={() => runJob([])}
              disabled={isRunning}
              aria-label="Refresh"
              title="Refresh"
            >
              <FiRefreshCw size={14} className={isRunning ? 'spin' : undefined} />
            </button>
          </div>
        </div>
      )}

      {action.type === 'policies' && showInfo && (
        <div className="ca-run--info ca-run__policies-info">{action.description}</div>
      )}

      {action.type === 'policies' && policies.length > 1 && (
        <input
          type="text"
          className="ca-run__input ca-run__policies-search"
          placeholder="Search by name or policy #…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}

      {action.type === 'report' && (
        <div className="ca-run__form">
          <button type="button" className="ca-run__btn" onClick={() => runJob([])} disabled={isRunning}>
            {isRunning ? <FiRefreshCw size={13} className="spin" /> : <FiPlay size={13} />}
            {isRunning ? 'Running…' : 'Run Report'}
          </button>
        </div>
      )}

      {action.type === 'quote' && QuoteForm && (
        <QuoteForm disabled={isRunning} onSubmit={(payload) => runJob([JSON.stringify({ ...payload, headed })])} />
      )}

      {run.phase === 'running' && (
        <div className="ca-run__status ca-run__status--progress">
          <FiRefreshCw size={12} className="spin" />
          {run.status.log.trim().split('\n').filter(Boolean).pop() ?? 'Starting…'}
        </div>
      )}

      {run.phase === 'running' && run.status.status === 'needs_attention' && (
        <div className="ca-run__status ca-run__status--attention">
          <FiAlertTriangle size={13} /> {run.status.attentionMessage ?? 'This carrier needs manual attention to continue.'}
        </div>
      )}

      {run.phase === 'done' && run.status.status === 'needs_manual_login' && (
        <div className="ca-run__status ca-run__status--attention">
          <FiAlertTriangle size={13} /> {run.status.attentionMessage ?? 'Log in to this carrier in the carrier browser, then retry.'}
          <div className="ca-run__form">
            <button type="button" className="ca-run__btn" onClick={handleOpenCarrierBrowser} disabled={browserOpening}>
              {browserOpening ? <FiRefreshCw size={13} className="spin" /> : <FiPlay size={13} />}
              {browserOpening ? 'Opening…' : 'Open carrier browser'}
            </button>
            <button type="button" className="ca-run__btn" onClick={() => runJob(lastArgs)}>
              <FiRefreshCw size={13} /> I'm logged in — retry
            </button>
          </div>
        </div>
      )}

      {run.phase === 'running' && run.status.status === 'needs_2fa_code' && (
        <div className="ca-run__status ca-run__status--attention">
          <FiAlertTriangle size={13} /> {run.status.attentionMessage ?? 'Enter the verification code the carrier sent.'}
          <form
            className="ca-run__form"
            onSubmit={(e) => { e.preventDefault(); handleSubmitTwoFactor(); }}
          >
            <input
              className="ca-run__input"
              placeholder="Verification code"
              value={twoFactorCode}
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={10}
              disabled={twoFactorSubmitting}
              onChange={(e) => setTwoFactorCode(e.target.value)}
            />
            <button type="submit" className="ca-run__btn" disabled={twoFactorSubmitting || !twoFactorCode.trim()}>
              {twoFactorSubmitting ? 'Submitting…' : 'Submit code'}
            </button>
          </form>
          {twoFactorError && <div>{twoFactorError}</div>}
        </div>
      )}

      {run.phase === 'error' && (
        <div className="ca-run__status ca-run__status--error">
          <FiAlertTriangle size={13} /> {run.message}
        </div>
      )}

      {run.phase === 'done' && (run.status.status === 'failed' || run.status.status === 'needs_attention') && (
        <div className="ca-run__status ca-run__status--error">
          <FiAlertTriangle size={13} /> {run.status.status === 'needs_attention' && run.status.attentionMessage
            ? `Run stopped: ${run.status.attentionMessage}`
            : 'Run failed — see log below.'}
          <details className="ca-run__log">
            <summary>Log</summary>
            <pre>{run.status.log.slice(-4000)}</pre>
          </details>
        </div>
      )}

      {run.phase === 'done' && run.status.status === 'completed' && action.type === 'quote' && run.status.quoteJson && (
        <div className="ca-run__status ca-run__status--success">
          <FiCheckCircle size={13} />
          <span>
            Quote {run.status.quoteJson.quoteNumber ?? '(number not shown)'} rated at ${run.status.quoteJson.totalPremium} for 12 months.
            {' '}Dwelling ${run.status.quoteJson.dwellingCoverage}; Progressive&apos;s replacement cost estimate ${run.status.quoteJson.replacementCostEstimate}.
            {Object.keys(run.status.quoteJson.packages).length > 0 &&
              ` Packages: ${Object.entries(run.status.quoteJson.packages).map(([name, price]) => `${name} $${price}`).join(', ')}.`}
            {' '}Preliminary — no claims or credit reports were ordered, and nothing was bound.
          </span>
        </div>
      )}

      {run.phase === 'done' && run.status.status === 'completed' && action.type === 'quote' && run.status.resultJson && (
        run.status.resultJson.status === 'ready_to_bind' ? (
          <div className="ca-run__status ca-run__status--success">
            <FiCheckCircle size={13} />
            <span>
              Quote {run.status.resultJson.policyNumber} is ready to bind: term premium {run.status.resultJson.termPremium ? `$${run.status.resultJson.termPremium}` : 'not shown'}, RCE {run.status.resultJson.rce ? `$${run.status.resultJson.rce}` : 'not shown'}. It was not bound — binding is a separate step in the carrier portal.
            </span>
          </div>
        ) : (
          <div className="ca-run__status ca-run__status--error">
            <FiAlertTriangle size={13} /> {run.status.resultJson.reason ?? `The quote ended in an unexpected state (${run.status.resultJson.status}).`}
          </div>
        )
      )}

      {run.phase === 'done' && run.status.status === 'completed' && !run.status.quoteJson && !run.status.resultJson && (action.type === 'document' || action.type === 'quote' || action.type === 'report') && (
        run.status.resultPath ? (
          <a
            className="ca-run__status ca-run__status--success"
            href={resolveDownloadUrl(run.status.resultPath)}
            target="_blank"
            rel="noreferrer"
          >
            <FiDownload size={13} /> {action.type === 'quote' ? 'Download quote' : action.type === 'report' ? 'Download report' : 'Download document'}
          </a>
        ) : (
          <div className="ca-run__status ca-run__status--error">
            <FiAlertTriangle size={13} /> Run completed, but no {action.type === 'quote' ? 'quote' : action.type === 'report' ? 'report' : 'document'} was found — see log below.
            <details className="ca-run__log">
              <summary>Log</summary>
              <pre>{run.status.log.slice(-4000)}</pre>
            </details>
          </div>
        )
      )}

      {run.phase === 'done' && run.status.status === 'completed' && action.type === 'lookup' && (() => {
        const rows = run.status.reportJson?.rows ?? [];
        if (run.status.resultPath && rows.length === 0) {
          return (
            <a className="ca-run__status ca-run__status--success" href={resolveDownloadUrl(run.status.resultPath)} target="_blank" rel="noreferrer">
              <FiDownload size={13} /> Download
            </a>
          );
        }
        if (rows.length === 0) {
          return <div className="ca-run__status ca-run__status--progress">{run.status.reportJson?.note ?? 'Nothing found.'}</div>;
        }
        const columns = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
        const longText = rows.some((r) => Object.values(r).some((v) => typeof v === 'string' && v.length > 90));
        if (longText) {
          // Pages like a policy's summary come back as paragraphs, not cells: show each row as labelled blocks.
          return (
            <div className="ca-run__policy-detail">
              {rows.map((r, i) => (
                <div key={i}>
                  {columns.map((c) => (r[c] == null || r[c] === '' ? null : (
                    <span key={c} className="ca-run__card-detail-row">
                      <span className="ca-run__card-detail-key">{c}</span>
                      <span>{String(r[c])}</span>
                    </span>
                  )))}
                </div>
              ))}
            </div>
          );
        }
        return (
          <div style={{ overflowX: 'auto' }}>
            <table className="ca-run__table">
              <thead>
                <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>{columns.map((c) => <td key={c}>{r[c] == null ? '' : String(r[c])}</td>)}</tr>
                ))}
              </tbody>
            </table>
            {run.status.reportJson?.note && <div className="ca-run__status ca-run__status--progress">{run.status.reportJson.note}</div>}
          </div>
        );
      })()}

      {run.phase === 'done' && run.status.status === 'completed' && action.type === 'policySearch' && (
        (run.status.policiesJson ?? []).length > 0 ? (
          <table className="ca-run__table">
            <thead>
              <tr><th>Policy #</th><th>Name</th><th>Product</th><th>State</th><th>Status</th></tr>
            </thead>
            <tbody>
              {(run.status.policiesJson ?? []).map((p) => (
                <tr key={p.policyNumber}>
                  <td>{p.policyNumber}</td><td>{p.name}</td><td>{String(p.product ?? '')}</td><td>{String(p.state ?? '')}</td><td>{String(p.status ?? '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="ca-run__status ca-run__status--progress">No policies found for that last name.</div>
        )
      )}

      {run.phase === 'done' && run.status.status === 'completed' && action.type === 'policyDocuments' && (
        (run.status.documentsJson ?? []).length > 0 ? (
          <table className="ca-run__table">
            <thead>
              <tr><th>Date</th><th>Document</th><th>Delivery</th></tr>
            </thead>
            <tbody>
              {(run.status.documentsJson ?? []).map((d, i) => (
                <tr key={i}><td>{d.date}</td><td>{d.type}</td><td>{d.delivery}</td></tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="ca-run__status ca-run__status--progress">No documents listed for that policy.</div>
        )
      )}

      {action.type === 'policies' && policies.length > 0 && filteredPolicies.length === 0 && (
        <div className="ca-run--info ca-run__policies-info">No policies match "{search}".</div>
      )}

      {action.type === 'policies' && filteredPolicies.length > 0 && (
        <select
          className="ca-run__select ca-run__policies-select"
          value={selectedPolicy ?? ''}
          onChange={(e) => {
            const policyNumber = e.target.value;
            setSelectedPolicy(policyNumber || null);
            if (policyNumber) onPolicySelected?.(policyNumber);
          }}
        >
          <option value="" disabled>Select a policy…</option>
          {filteredPolicies.map((p) => (
            <option key={p.policyNumber} value={p.policyNumber}>{p.policyNumber} — {p.name}</option>
          ))}
        </select>
      )}

      {selectedPolicyRecord && (() => {
        const extraFields = Object.entries(selectedPolicyRecord).filter(([key]) => key !== 'policyNumber' && key !== 'name');
        return (
          <div className="ca-run__policy-detail">
            <div className="ca-run__policy-detail-header">
              {selectedPolicyRecord.policyNumber} — {selectedPolicyRecord.name}
            </div>
            {extraFields.length > 0 ? (
              extraFields.map(([key, value]) => (
                <span key={key} className="ca-run__card-detail-row">
                  <span className="ca-run__card-detail-key">{key}</span>
                  <span>{String(value)}</span>
                </span>
              ))
            ) : (
              <span className="ca-run__card-detail-row">No additional details available.</span>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default CarrierActionRunner;
